import * as React from "react";

import { cn, cva } from "../../lib";
import { CheckboxPrimitive } from "../../primitives/checkbox";
import { iconSize } from "../../tokens";
import { Icon } from "../icons";
import type { CheckboxProps } from "./types";

const checkboxVariants = cva(
  "border-luce-neutral-border-control web:focus-visible:ring-luce-brand-primaryBorder rounded-radius-sm web:focus-visible:ring-focus web:focus-visible:ring-offset-focus border-1-5 peer shrink-0 outline-none transition-colors",
  {
    variants: {
      // Figma draws each box inside a frame; the margin is that frame, in px
      // like the box. Small is 18 (desktop 22) in 20 (24); Medium is 20 (24) in
      // 24 (28), Ardhi Q3.
      size: {
        sm: "h-checkbox-sm w-checkbox-sm md:h-checkbox-sm-dt md:w-checkbox-sm-dt m-px",
        md: "h-checkbox-md w-checkbox-md md:h-checkbox-md-dt md:w-checkbox-md-dt m-[2px]",
      },
      checked: {
        true: "border-luce-brand-primary bg-luce-brand-primary text-luce-brand-onPrimary",
        false: "bg-luce-neutral-background-default",
      },
      disabled: {
        true: "pointer-events-none",
        false: "",
      },
    },
    compoundVariants: [
      // Hover (web): a brand/primary halo at 10%, 2px outside the box (Figma
      // 68:8470, 68:8446); unchecked, the border stays border-control, as
      // Figma's Hovered binds it. A keyboard focus ring comes later and wins.
      {
        disabled: false,
        checked: false,
        class: "web:hover:ring-halo web:hover:ring-luce-brand-primary/10",
      },
      {
        disabled: false,
        checked: true,
        class:
          "web:hover:border-luce-brand-primaryHover web:hover:bg-luce-brand-primaryHover web:hover:ring-halo web:hover:ring-luce-brand-primary/10",
      },
      {
        disabled: true,
        checked: false,
        class: "border-luce-neutral-border-lowEmphasis bg-luce-neutral-background-attenuated",
      },
      {
        disabled: true,
        checked: true,
        class: "border-luce-brand-primary bg-luce-brand-primary opacity-disabled",
      },
    ],
    defaultVariants: {
      size: "md",
      checked: false,
      disabled: false,
    },
  },
);

/**
 * Checkbox atom — applies Luce design (CVA) to the Checkbox primitive.
 * Single file, multiplatform via primitive resolution.
 */
const Checkbox = React.forwardRef<React.ComponentRef<typeof CheckboxPrimitive>, CheckboxProps>(
  (
    {
      checked,
      indeterminate = false,
      onCheckedChange,
      disabled = false,
      size = "md",
      className,
      ...rest
    },
    ref,
  ) => (
    <CheckboxPrimitive
      ref={ref}
      checked={indeterminate ? "indeterminate" : checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      // Indeterminate shares the checked fill
      className={cn(
        checkboxVariants({ size, checked: checked || indeterminate, disabled }),
        className,
      )}
      // Native paints the checked fill on the indicator, which covers the box
      indicatorClassName="native:bg-luce-brand-primary flex items-center justify-center text-current"
      {...rest}
    >
      <Icon
        name={indeterminate ? "Minus" : "Check"}
        size={iconSize.xs}
        weight="bold"
        className="text-luce-brand-onPrimary native:mt-spacing-xxs"
      />
    </CheckboxPrimitive>
  ),
);

Checkbox.displayName = "Checkbox";

export { Checkbox };
